import { useState, useRef, useCallback, useMemo } from 'react';
import { usePostHog } from 'src/lib/posthog';
import { MentionData, MentionWithDisplayName } from './useMentionUsers';

interface PendingSubmission {
  allMentions: MentionData[];
  callback: (mentions: MentionData[]) => void;
}

interface CurrentShare {
  email: string;
  role: number;
}

/**
 * Filters mentions to find those that don't have sufficient access.
 * A mention "needs access" if the user either isn't shared with at all,
 * or their current permission level is below the required minimum.
 */
export function getMentionsNeedingAccess(
  mentions: MentionData[],
  currentShares: CurrentShare[],
  minimumPermission: number
): MentionData[] {
  const shareMap = new Map(currentShares.map((s) => [s.email.toLowerCase(), s.role]));
  return mentions.filter((m) => {
    const email = m.email?.toLowerCase();
    if (!email) return false;
    const existingRole = shareMap.get(email);
    return existingRole === undefined || existingRole < minimumPermission;
  });
}

/**
 * Hook to manage the mention-share flow. When a user submits content with
 * @mentions, this hook checks which mentioned users don't have sufficient
 * access and opens the share modal directly (with a mention banner) so
 * the user can grant appropriate access or skip.
 *
 * @param currentShares - Array of { email, role } for current shares
 * @param minimumPermission - The minimum permission level needed for the feature
 */
export function useMentionShareFlow(currentShares: CurrentShare[], minimumPermission: number) {
  const posthog = usePostHog();
  const [showShareModal, setShowShareModal] = useState(false);
  const [mentionsNeedingAccess, setMentionsNeedingAccess] = useState<MentionWithDisplayName[]>([]);
  const [shareModalEmails, setShareModalEmails] = useState<string[]>([]);
  const pendingRef = useRef<PendingSubmission | null>(null);

  /**
   * Check mentions before submitting. If all have sufficient access, calls
   * the callback immediately. Otherwise opens the share modal with the
   * mention banner.
   */
  const checkAndSubmit = useCallback(
    (mentions: MentionData[], submitCallback: (mentions: MentionData[]) => void) => {
      if (!mentions.length) {
        submitCallback(mentions);
        return;
      }

      const needAccess = getMentionsNeedingAccess(mentions, currentShares, minimumPermission);

      // Track tagged mentions regardless of whether share changes are needed.
      posthog?.capture('mention_tagged', {
        mentionCount: mentions.length,
        mentionsNeedingAccessCount: needAccess.length,
        requiresShareFlow: needAccess.length > 0,
        minimumPermission
      });

      if (needAccess.length === 0) {
        submitCallback(mentions);
        return;
      }

      pendingRef.current = { allMentions: mentions, callback: submitCallback };
      setMentionsNeedingAccess(needAccess);
      setShareModalEmails(needAccess.map((m) => m.email!).filter(Boolean));
      setShowShareModal(true);
    },
    [currentShares, minimumPermission]
  );

  /** Called after the user successfully shares via the modal.
   *  Only submits mentions that already had access or were actually shared. */
  const handleShareComplete = useCallback(
    (addedEmails: string[]) => {
      setShowShareModal(false);
      if (pendingRef.current) {
        const addedSet = new Set(addedEmails.map((e) => e.toLowerCase()));
        const needAccessEmails = new Set(mentionsNeedingAccess.map((m) => m.email?.toLowerCase()));
        const validMentions = pendingRef.current.allMentions.filter((m) => {
          const email = m.email?.toLowerCase();
          if (!email) return true;
          if (!needAccessEmails.has(email)) return true;
          return addedSet.has(email);
        });
        pendingRef.current.callback(validMentions);
        pendingRef.current = null;
      }
      setMentionsNeedingAccess([]);
      setShareModalEmails([]);
    },
    [mentionsNeedingAccess]
  );

  /** Called when the modal is closed without sharing. Does NOT submit — returns user to the original form. */
  const handleShareModalClose = useCallback(() => {
    setShowShareModal(false);
    pendingRef.current = null;
    setMentionsNeedingAccess([]);
    setShareModalEmails([]);
  }, []);

  return useMemo(
    () => ({
      checkAndSubmit,
      showShareModal,
      mentionsNeedingAccess,
      shareModalEmails,
      handleShareComplete,
      handleShareModalClose
    }),
    [
      checkAndSubmit,
      showShareModal,
      mentionsNeedingAccess,
      shareModalEmails,
      handleShareComplete,
      handleShareModalClose
    ]
  );
}
